import type {RepoLayoutData} from '@github-ui/dotcom-schema/types/repos/v1/repo_layout'

/**
 * {@link repoLayoutRoute} delivers a `RepoLayoutData` proto message carrying the fingerprinted
 * URLs of the global `javascript_bundle`/`stylesheet_bundle`s the sub-app controllers declare in
 * Rails. They load on a hard navigation (via the controller's bundle declaration) but NOT on a
 * React soft-navigation within the repo app. The maps are captured once from the initial
 * (hard-load) payload, so the URLs always match the running asset version — see RepoLayout for
 * the per-route injection.
 *
 * `js` and `css` are separate maps because a name can be both (e.g. `pull-requests` is a
 * `javascript_bundle` and a `stylesheet_bundle`). Each name maps to a `StringList` (proto3 map
 * values can't be `repeated`), so a bundle can expand to several chunk URLs.
 */

/**
 * Route id → the global JS/CSS bundles that route's Rails controller/action declares.
 * Because `useRouteMatches()` returns the layout AND leaf matches, unconditional bundles are
 * keyed to the sub-app layout route, and action-gated bundles (`only:`/`unless:`) are keyed to
 * the specific leaf routes so they load only where Rails would load them. Injected while a
 * matched route is active (JS as deduped `<script async>`, CSS as render-blocking
 * `<link rel="stylesheet" precedence>`).
 *
 * ⚠️ Keying too narrowly UNDER-loads (the missing-asset bug returns on that route), so the
 * route↔action mapping below mirrors the Rails controllers exactly — verify it when sub-app
 * routes change.
 */
export const ASSETS_BY_ROUTE: Record<string, {js?: readonly string[]; css?: readonly string[]}> = {
  releasesIndexRoute: {js: ['repositories'], css: ['releases']},
  tagsIndexRoute: {js: ['repositories'], css: ['releases']},

  // ===== code-view =====
  // `code` (stylesheet) and `code-menu` (nav) are declared on every code-view controller, so
  // they live on the shared code-view layout instead of being repeated per leaf. (`code-menu`
  // is absent only from the tree-delete confirmation page — a harmless extra there.)
  codeViewLayoutRoute: {js: ['code-menu'], css: ['code']},

  // Blob writes: `editor` is `only` edit/new/delete; `repositories`+`diffs` load `unless` a
  // React view (blame/edit/show) — i.e. only on new/delete. (Blob show/blame need nothing
  // beyond the shared layout.)
  codeViewEditRoute: {js: ['editor']},
  codeViewNewRoute: {js: ['editor', 'repositories', 'diffs']},
  codeViewDeleteRoute: {js: ['editor', 'repositories', 'diffs']},

  // Files controller (overview + subdirectory): `codespaces` + `repositories`.
  codeViewRepoRoute: {js: ['codespaces', 'repositories']},
  codeViewTreeRoute: {js: ['codespaces', 'repositories']},
  // Tree controller (delete): only `repositories`.
  codeViewTreeDeleteRoute: {js: ['repositories']},

  // ===== commits =====
  // Both controllers declare `code`; list routes use `repositories`, while detail routes use `diffs`.
  commitsLayoutRoute: {css: ['code']},
  commitsRoute: {js: ['repositories']},
  commitsRefRoute: {js: ['repositories']},
  commitRoute: {js: ['diffs']},
  commitPathRoute: {js: ['diffs']},

  // ===== actions-workflows =====
  // ActionsController declares both bundles for every onboarded workflow-list route.
  workflowsLayoutRoute: {js: ['actions'], css: ['actions']},

  // ===== pull-requests =====
  // Unconditional across every PR tab.
  pullRequestsLayoutRoute: {
    js: ['codespaces', 'diffs', 'code-menu', 'pull-requests-list'],
    css: ['code', 'pull-requests', 'copilot-code-review'],
  },
  // Rails `show` is the bare /pull/:id → conversations route. `scanning` is `only`
  // show/files/checks; `pull-requests` (js) is `only` show; `actions` (css) is `only`
  // show/files/checks/changes/commits.
  pullRequestsConversationsRoute: {js: ['scanning', 'pull-requests'], css: ['actions']},
  pullRequestsChecksRoute: {js: ['scanning'], css: ['actions']},
  pullRequestsChangesRoute: {css: ['actions']},
  pullRequestsChangesWithRangeRoute: {css: ['actions']},
  pullRequestsCommitsRoute: {css: ['actions']},
  pullRequestsCommitsWithRangeRoute: {css: ['actions']},
}

/**
 * Given the ids of the currently-active route matches and the layout payload, returns the
 * deduped lists of script `src`s and stylesheet `href`s to inject for those routes.
 */
export function assetsForRoutes(
  routeIds: ReadonlyArray<string | undefined>,
  payload: RepoLayoutData | undefined,
): {js: string[]; css: string[]} {
  const resolve = (kind: 'js' | 'css') => {
    const urlsByName = payload?.[kind] ?? {}
    const neededBundles = new Set(routeIds.flatMap(id => ASSETS_BY_ROUTE[id ?? '']?.[kind] ?? []))
    return [...new Set([...neededBundles].flatMap(name => urlsByName[name]?.values ?? []))]
  }
  return {js: resolve('js'), css: resolve('css')}
}
